import React, { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Calendar, User, ArrowRight, Share2, MessageCircle } from "lucide-react";
import { ARTICLES, getArticleBySlug } from "../data/articles";
import { waUrl } from "../lib/api";
import Logo from "./Logo";
import Footer from "./Footer";
import WhatsAppButton from "./WhatsAppButton";

export default function ArticleDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const article = getArticleBySlug(slug);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    if (!article) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white" data-testid="article-not-found">
                <div className="text-center">
                    <h1 className="font-display text-2xl font-bold text-[#0A1A2F]">Artikel tidak ditemukan, Kak.</h1>
                    <button onClick={() => navigate("/")} className="mt-4 inline-flex items-center gap-2 text-[#F5A623] font-semibold">
                        <ArrowLeft className="w-4 h-4" /> Kembali ke beranda
                    </button>
                </div>
            </div>
        );
    }

    const Icon = article.Icon;
    const related = ARTICLES.filter((a) => a.slug !== article.slug).slice(0, 3);

    const handleShare = async () => {
        const url = window.location.href;
        if (navigator.share) {
            try {
                await navigator.share({ title: article.title, url });
            } catch (e) {
                // AbortError = user cancelled share sheet; safe to ignore
                if (e?.name !== "AbortError") {
                    console.warn("Share failed:", e);
                }
            }
        } else {
            navigator.clipboard?.writeText(url);
        }
    };

    return (
        <div className="bg-white min-h-screen" data-testid="article-detail-page">
            <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-xl border-b border-slate-100">
                <div className="max-w-5xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
                    <Link to="/" className="flex-shrink-0">
                        <Logo size={40} />
                    </Link>
                    <button
                        onClick={() => navigate("/")}
                        data-testid="back-to-home"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-[#0A1A2F] transition"
                    >
                        <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Kembali ke Beranda</span>
                    </button>
                </div>
            </header>

            {/* Hero */}
            <section className={`relative bg-gradient-to-br ${article.accent} text-white overflow-hidden`}>
                <div className="absolute inset-0 opacity-20 bg-grain pointer-events-none" />
                <div className="absolute -top-32 -right-32 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
                <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24 relative">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 text-xs font-bold uppercase tracking-widest mb-6" data-testid="article-category">
                            <Icon className="w-3.5 h-3.5" /> {article.category}
                        </div>
                        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight" data-testid="article-title">
                            {article.title}
                        </h1>
                        <div className="mt-6 flex flex-wrap items-center gap-4 sm:gap-6 text-sm text-white/85">
                            <span className="inline-flex items-center gap-1.5"><User className="w-4 h-4" /> {article.author}</span>
                            <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {article.published}</span>
                            <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" /> {article.read}</span>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Body */}
            <article className="max-w-3xl mx-auto px-6 md:px-12 py-14 md:py-20">
                <p className="text-lg text-slate-600 leading-relaxed font-medium border-l-4 border-[#F5A623] pl-5 italic" data-testid="article-excerpt">
                    {article.excerpt}
                </p>

                <div className="mt-10 space-y-10" data-testid="article-body">
                    {article.sections.map((s, i) => (
                        <motion.section
                            key={s.heading}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.5, delay: i * 0.05 }}
                        >
                            <h2 className="font-display text-xl sm:text-2xl font-bold text-[#0A1A2F] tracking-tight">{s.heading}</h2>
                            <div className="mt-3 text-[15px] sm:text-base text-slate-700 leading-relaxed whitespace-pre-line">{s.body}</div>
                        </motion.section>
                    ))}
                </div>

                {/* Share + CTA */}
                <div className="mt-14 pt-8 border-t border-slate-200 flex flex-col sm:flex-row gap-4 items-stretch sm:items-center justify-between">
                    <button
                        onClick={handleShare}
                        data-testid="share-btn"
                        className="inline-flex items-center justify-center gap-2 border-2 border-slate-200 hover:border-[#0A1A2F] rounded-full px-5 py-2.5 text-sm font-semibold text-[#0A1A2F] transition"
                    >
                        <Share2 className="w-4 h-4" /> Bagikan artikel
                    </button>
                    <a
                        href={waUrl("Halo Kak Gantari Edu, saya baru baca artikel di website kalian dan ingin diskusi lebih lanjut.")}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="article-cta-wa"
                        className="inline-flex items-center justify-center gap-2 bg-[#F5A623] text-[#0A1A2F] hover:bg-[#D98F1B] rounded-full px-6 py-3 text-sm font-bold transition shadow-[0_4px_14px_0_rgba(245,166,35,0.35)]"
                    >
                        <MessageCircle className="w-4 h-4" /> Diskusi langsung dengan Mentor
                    </a>
                </div>
            </article>

            {/* Related */}
            <section className="bg-[#F8FAFC] py-16 md:py-20">
                <div className="max-w-6xl mx-auto px-6 md:px-12">
                    <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#0A1A2F] tracking-tight">Artikel lain yang relevan</h2>
                    <div className="mt-8 grid md:grid-cols-3 gap-5">
                        {related.map((a) => {
                            const RIcon = a.Icon;
                            return (
                                <Link
                                    key={a.slug}
                                    to={`/artikel/${a.slug}`}
                                    className="group bg-white border border-slate-200 rounded-3xl overflow-hidden hover:shadow-lg hover:border-[#F5A623]/40 transition-all"
                                    data-testid={`related-${a.slug}`}
                                >
                                    <div className={`relative h-32 bg-gradient-to-br ${a.accent}`}>
                                        <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/90 text-[10px] font-bold uppercase tracking-wider text-[#0A1A2F]">
                                            {a.category}
                                        </div>
                                        <div className="absolute bottom-3 right-3 w-10 h-10 rounded-xl bg-white/15 backdrop-blur-md flex items-center justify-center border border-white/20">
                                            <RIcon className="w-5 h-5 text-white" />
                                        </div>
                                    </div>
                                    <div className="p-5">
                                        <h3 className="font-display font-semibold text-base text-[#0A1A2F] leading-snug group-hover:text-[#F5A623] transition-colors line-clamp-2">{a.title}</h3>
                                        <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500">
                                            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {a.read}</span>
                                            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            <Footer />
            <WhatsAppButton />
        </div>
    );
}
